// 📝 사용자 설문 모달 (2026-09-11 사용자 "상단에 업데이트처럼 임시 버튼 하나 — 만족도·개선점·추가 희망, 회사·부서·직급만 수집")
//   v2 29문항(필수 24 + 선택 5) · 섹션 A~D · 약 5분. 회사·부서·직급·직책은 서버가 GW 문서에서 붙인다(여기선 안내만).
//   rating 양끝 라벨은 q.scale('sat' 불만~만족 / 'agree' 전혀 아님~매우 그렇다).
//   문항 정의 = src/survey/questions.ts(서버 스키마와 동일 id). 제출 = api/survey.submitSurvey.
//   🔤 굵은 글자 금지 (2026-09-13 원격 "설문의 글자 볼드 빼고, 안 굵게") — 문항 29개가 전부 semibold 라 '볼드 벽'이 됐다.
//      위계는 굵기 대신 **색(문항=primary·보조=subtle) · 섹션 파란색+2px 밑줄 · 구분선 · 간격(py-4/mt-7)** 이 맡는다.
//      문항·섹션·안내문에 font-bold/semibold 를 다시 넣지 말 것.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DetailModal from '../common/DetailModal';
import { Icon } from '../icons';
import { tptAlert, humanizeError } from '../../stores/modalStore';
import { SURVEY_QUESTIONS, SURVEY_SECTIONS, type SurveyQuestion } from '../../survey/questions';
import { submitSurvey, useSurveyState } from '../../api/survey';

type Answers = Record<string, unknown>;

// 선택지·척도 버튼 — 꺼진 상태는 `tpt-survey-opt`(index.css)가 테마별 배경·테두리를 준다.
//   다크에서 bg-gray-50 은 모달(elevated #242220)보다 어두워 칩이 '구멍'처럼 파묻히고 테두리(.08 알파)도 거의 안 보였다 (2026-09-13 사용자 "가독성이 떨어짐").
const chip = (on: boolean) => `rounded-full border px-3 py-1.5 text-ui-sm transition ${on ? 'border-class-blue bg-class-blue text-white' : 'tpt-survey-opt'}`;
const scaleBtn = (on: boolean) => `h-9 min-w-9 rounded-lg border px-1 text-ui-sm transition ${on ? 'border-class-blue bg-class-blue text-white' : 'tpt-survey-opt'}`;

function Question({ q, value, other, onChange, onOther, error }: { q: SurveyQuestion; value: unknown; other: string; onChange: (v: unknown) => void; onOther: (s: string) => void; error?: string }) {
	const { t } = useTranslation();
	const label = t(`survey.q.${q.id}`);
	const arr = Array.isArray(value) ? value as string[] : [];
	return (
		<div className="py-4" data-testid={`survey-${q.id}`}>
			<div className="mb-2.5 text-ui-sm leading-relaxed text-fg">
				{label}{q.required && <span className="ml-1 text-red-600">*</span>}
				{q.type === 'multi' && q.max && <span className="ml-2 text-ui-xs font-normal text-fg-subtle">{t('survey.maxPick', { n: q.max })}</span>}
			</div>
			{(q.type === 'multi' || q.type === 'single') && (
				<div className="flex flex-wrap gap-1.5">
					{q.options!.map((o) => {
						const on = q.type === 'multi' ? arr.includes(o) : value === o;
						return (
							<button key={o} type="button" className={chip(on)} data-testid={`survey-${q.id}-${o}`} onClick={() => {
								if (q.type === 'single') { onChange(on ? undefined : o); return; }
								if (on) { onChange(arr.filter((x) => x !== o)); return; }
								// 'none'(없음) 은 배타 — 고르면 나머지를 비우고, 다른 것을 고르면 none 을 뺀다 (q19)
								if (o === 'none') { onChange(['none']); return; }
								const base = arr.filter((x) => x !== 'none');
								if (!q.max || base.length < q.max) onChange([...base, o]);
							}}>{t(`survey.o.${q.id}.${o}`)}</button>
						);
					})}
				</div>
			)}
			{q.type === 'multi' && q.other && arr.includes('other') && (
				<input value={other} onChange={(e) => onOther(e.target.value.slice(0, 200))} placeholder={t('survey.otherPlaceholder')} maxLength={200}
					className="tpt-input mt-2 w-full px-2.5 py-1.5 text-ui-sm focus:border-class-blue focus:outline-none" data-testid={`survey-${q.id}-other`} />
			)}
			{q.type === 'rating' && (
				<div className="flex flex-wrap items-center gap-1.5">
					<span className="mr-1 text-ui-sm text-fg-muted">{t(q.scale === 'agree' ? 'survey.agreeLow' : 'survey.ratingLow')}</span>
					{[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" className={scaleBtn(value === n)} onClick={() => onChange(value === n ? undefined : n)}>{n}</button>)}
					<span className="ml-1 text-ui-sm text-fg-muted">{t(q.scale === 'agree' ? 'survey.agreeHigh' : 'survey.ratingHigh')}</span>
					{q.skippable && <button type="button" className={`${chip(value === 0)} ml-2`} onClick={() => onChange(value === 0 ? undefined : 0)}>{t('survey.skip')}</button>}
				</div>
			)}
			{q.type === 'nps' && (
				<div>
					<div className="flex flex-wrap gap-1">
						{Array.from({ length: 11 }, (_, n) => <button key={n} type="button" className={scaleBtn(value === n)} onClick={() => onChange(value === n ? undefined : n)}>{n}</button>)}
					</div>
					<div className="mt-1 flex justify-between text-ui-sm text-fg-muted"><span>{t('survey.npsLow')}</span><span>{t('survey.npsHigh')}</span></div>
				</div>
			)}
			{q.type === 'text' && (
				<div>
					<textarea value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value.slice(0, q.maxLen || 1000))} rows={q.required ? 3 : 2}
						maxLength={q.maxLen || 1000} placeholder={t(`survey.ph.${q.id}`)} className="tpt-input w-full resize-y px-2.5 py-1.5 text-ui-sm focus:border-class-blue focus:outline-none" />
					<div className="mt-0.5 text-right text-ui-xs text-fg-subtle">{(typeof value === 'string' ? value.length : 0)}/{q.maxLen || 1000}</div>
				</div>
			)}
			{error && <div className="mt-1 text-ui-xs text-red-600" data-testid={`survey-${q.id}-error`}>{error}</div>}
		</div>
	);
}

export default function SurveyModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone?: () => void }) {
	const { t, i18n } = useTranslation();
	const [answers, setAnswers] = useState<Answers>({});
	const [others, setOthers] = useState<Record<string, string>>({});
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [busy, setBusy] = useState(false);
	const [done, setDone] = useState(false);

	const st = useSurveyState();
	const endsAt = st?.endsAt || null;
	const dLeft = st?.daysLeft ?? null;
	const bySection = useMemo(() => SURVEY_SECTIONS.map((s) => ({ s, qs: SURVEY_QUESTIONS.filter((q) => q.section === s) })), []);
	const answeredCount = SURVEY_QUESTIONS.filter((q) => { const v = answers[q.id]; return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0); }).length;

	const validate = (): boolean => {
		const e: Record<string, string> = {};
		for (const q of SURVEY_QUESTIONS) {
			const v = answers[q.id];
			const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0) || (typeof v === 'string' && !v.trim());
			if (q.required && empty) e[q.id] = t('survey.errRequired');
		}
		setErrors(e);
		if (Object.keys(e).length) {
			const first = document.querySelector(`[data-testid="survey-${Object.keys(e)[0]}"]`);
			first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
			return false;
		}
		return true;
	};
	const submit = async () => {
		if (busy || !validate()) return;
		setBusy(true);
		try {
			const payload: Answers = {};
			for (const q of SURVEY_QUESTIONS) {
				const v = answers[q.id];
				if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) continue;
				payload[q.id] = typeof v === 'string' ? v.trim() : v;
				if (q.other && Array.isArray(v) && (v as string[]).includes('other') && others[q.id]?.trim()) payload[q.id + '_other'] = others[q.id].trim();
			}
			await submitSurvey(payload, i18n.language);
			setDone(true);
			onDone?.();
		} catch (err) { void tptAlert(humanizeError(err)); }
		finally { setBusy(false); }
	};

	// 🔴 bodyClassName 필수 — scrollBody 래퍼에는 기본 여백이 없다. 헤더·푸터만 px-5 라 본문이 왼쪽에 붙어
	//    '짤린 것처럼' 보였다 (2026-09-13 사용자). 다른 모달과 같은 px-5 py-4 관행을 따른다.
	return (
		<DetailModal open={open} onClose={onClose} width={640} closeOnBackdrop={false} scrollBody bodyClassName="px-5 py-4" testId="survey-modal"
			title={`📝 ${t('survey.title')}`}
			footer={done ? (
				<div className="flex justify-end"><button type="button" onClick={onClose} className="tpt-btn-primary rounded-md px-4 py-1.5 text-ui-sm" data-testid="survey-close">{t('survey.closeBtn')}</button></div>
			) : (
				<div className="flex items-center justify-between gap-2">
					<span className="text-ui-xs text-fg-subtle">{t('survey.progress', { n: answeredCount, total: SURVEY_QUESTIONS.length })}</span>
					<div className="flex gap-2">
						<button type="button" onClick={onClose} className="rounded-md bg-surface-muted px-3 py-1.5 text-ui-sm text-fg hover:bg-hover-strong">{t('survey.later')}</button>
						<button type="button" onClick={() => void submit()} disabled={busy} className="tpt-btn-primary rounded-md px-4 py-1.5 text-ui-sm disabled:opacity-50" data-testid="survey-submit">{busy ? t('survey.submitting') : t('survey.submit')}</button>
					</div>
				</div>
			)}>
			{done ? (
				<div className="py-8 text-center" data-testid="survey-thanks">
					<div className="flex justify-center text-class-blue"><Icon name="check-circle" size={32} /></div>
					<div className="mt-2 text-ui-base text-fg">{t('survey.thanksTitle')}</div>
					<div className="mt-1 text-ui-sm text-fg-subtle">{t('survey.thanksBody')}</div>
				</div>
			) : (
				<div>
					<div className="rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-ui-sm leading-relaxed text-blue-800">
						<div>{t('survey.intro')}</div>
						{/* 📅 마감일 — 기간이 정해진 회차만 (2026-09-13 사용자 "설문기간이 없네") */}
						{endsAt && <div className="mt-1.5" data-testid="survey-period"><Icon name="calendar" size={14} className="inline-block align-[-2px] mr-1" />{t('survey.periodUntil', { date: endsAt })}{typeof dLeft === 'number' && dLeft >= 0 ? ` (${dLeft <= 0 ? t('survey.ddayToday') : t('survey.dday', { n: dLeft })})` : ''}</div>}
						<div className="mt-1.5">{t('survey.privacy')}</div>
					</div>
					{/* 섹션 간격을 넉넉히(mt-7) — 문항이 29개라 붙여 놓으면 어디까지가 한 묶음인지 안 보인다 (2026-09-13 사용자 "너무 붙어서") */}
					{bySection.map(({ s, qs }) => (
						<section key={s} className="mt-7">
							<h3 className="tpt-survey-sec border-b-2 pb-1.5 text-ui-sm text-class-blue">{t(`survey.sec.${s}`)}</h3>
							<div className="tpt-survey-list divide-y">
								{qs.map((q) => (
									<Question key={q.id} q={q} value={answers[q.id]} other={others[q.id] || ''} error={errors[q.id]}
										onChange={(v) => { setAnswers((a) => ({ ...a, [q.id]: v })); if (errors[q.id]) setErrors((e) => { const { [q.id]: _x, ...rest } = e; return rest; }); }}
										onOther={(sv) => setOthers((o) => ({ ...o, [q.id]: sv }))} />
								))}
							</div>
						</section>
					))}
				</div>
			)}
		</DetailModal>
	);
}
